import {
  fallbackLanguage,
  languageStorageKey,
  type SupportedLanguage,
  supportedLanguages,
} from "./constants";

export function normalizeSupportedLanguage(
  language: string | null | undefined,
): SupportedLanguage | undefined {
  if (!language) return undefined;
  const trimmed = language.trim();
  if (supportedLanguages.includes(trimmed as SupportedLanguage)) {
    return trimmed as SupportedLanguage;
  }

  const baseLanguage = trimmed.split("-")[0];
  if (supportedLanguages.includes(baseLanguage as SupportedLanguage)) {
    return baseLanguage as SupportedLanguage;
  }

  if (baseLanguage === "zh") {
    return "zh-TW";
  }

  return undefined;
}

export function coerceSupportedLanguage(
  language: string | null | undefined,
): SupportedLanguage {
  return normalizeSupportedLanguage(language) ?? fallbackLanguage;
}

export function readStoredLanguagePreference(): SupportedLanguage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return normalizeSupportedLanguage(
      window.localStorage.getItem(languageStorageKey),
    );
  } catch {
    return undefined;
  }
}

export function persistLanguagePreference(language: SupportedLanguage): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(languageStorageKey, language);
  } catch {
    // Best-effort persistence.
  }
}

function resolveNavigatorLanguage(): SupportedLanguage | undefined {
  if (typeof navigator === "undefined") return undefined;
  const languages =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  for (const language of languages) {
    const normalized = normalizeSupportedLanguage(language);
    if (normalized) return normalized;
  }
  return undefined;
}

export function resolveDetectedLanguagePreference(options: {
  currentLanguage?: string;
  resolvedLanguage?: string;
}): SupportedLanguage {
  return (
    readStoredLanguagePreference() ??
    normalizeSupportedLanguage(options.currentLanguage) ??
    normalizeSupportedLanguage(options.resolvedLanguage) ??
    resolveNavigatorLanguage() ??
    fallbackLanguage
  );
}
